import type { ClienteApi, ConVersion } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import type { components, operations } from '../tipos/eventos-entradas';
import type { components as Listas } from '../tipos/listas-rrpp';
import type { components as Pases } from '../tipos/pases-acceso';

/**
 * Operaciones de «Entradas» (Pedidos 10.6 e Informes 10.8) y de «Taquilla» (10.7), con su `operationId` y los tipos
 * generados de `eventos-entradas.yaml`, más lo que esas pantallas leen de Pases y Acceso (reenviar un pase, la asistencia
 * del grupo de acceso y las llegadas por hora) y de Listas y RRPP (invitados del evento). Esos módulos pueden responder
 * 403 o 404 a quien no tiene su permiso: las pantallas lo toleran pieza a pieza.
 *
 * - Lo que mueve dinero o avisa a alguien (cancelar, reintentar el reembolso, vender, reenviar) lleva `Idempotency-Key`.
 * - Corregir el nombre y cancelar van con la versión leída del pedido (`If-Match` con el `ETag` de getTicketOrder).
 * - El panel **nunca** calcula dinero: el subtotal, los gastos de gestión, el total, lo devuelto, los ingresos y lo que
 *   cuesta una venta en taquilla (`quoteTicketOrder`) los da la API.
 */
type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type Pedido = Esquema<'Order'>;
export type EstadoPedido = Esquema<'OrderStatus'>;
export type CanalPedido = Esquema<'OrderChannel'>;
export type EstadoReembolso = Esquema<'RefundStatus'>;
export type FormaPagoPedido = Esquema<'PaymentMethod'>;
export type Entrada = Esquema<'Ticket'>;
export type ListaPedidos = Esquema<'OrderList'>;
export type CancelarPedido = Esquema<'OrderCancel'>;
export type Evento = Esquema<'Event'>;
export type ListaEventos = Esquema<'EventList'>;
export type EstadoEvento = Esquema<'EventStatus'>;
export type TipoEntrada = Esquema<'TicketType'>;
export type EstadoTaquilla = Esquema<'BoxOfficeStatus'>;
export type VentaTaquilla = Esquema<'BoxOfficeSaleCreate'>;
export type PedidoCreado = Esquema<'OrderCreated'>;
export type Presupuesto = Esquema<'OrderQuote'>;
export type LineaPedida = Esquema<'OrderLineRequest'>;
export type ResumenVentas = Esquema<'EventSalesSummary'>;
export type GrupoAcceso = Pases['schemas']['AccessGroupDetail'];
export type ResumenEscaneos = Pases['schemas']['ScanSummary'];
export type Reenvio = Pases['schemas']['ResendAccepted'];
export type ResumenInvitados = Listas['schemas']['EventGuestSummary'];
export type ListaDeInvitados = Listas['schemas']['GuestList'];

type ConsultaPedidosApi = NonNullable<operations['listTicketOrders']['parameters']['query']>;

/** Filtros de «Pedidos» (10.6): evento, estado, canal (G510, G540, G572), localizador y reembolso; cursor sin total (G121). */
export interface FiltrosPedidos extends Pagina {
    event_id?: string;
    /** Uno o varios estados (con comas, G827). */
    status?: EstadoPedido | readonly EstadoPedido[];
    channel?: CanalPedido;
    customer_id?: string;
    reference?: string;
    refund_status?: EstadoReembolso;
    sort?: ConsultaPedidosApi['sort'];
}

/** Filtros de listEvents. */
export interface FiltrosEventos extends Pagina {
    /** Uno o varios estados (con comas, G827). */
    status?: EstadoEvento | readonly EstadoEvento[];
    location_id?: string;
    /** Eventos que terminan después de este momento. */
    from?: string;
    /** Eventos que empiezan antes de este momento. */
    to?: string;
}

const r = (businessId: string, resto: string): `/v1/${string}` => `${negocio(businessId)}${resto}`;
const id = (valor: string) => encodeURIComponent(valor);

export function operacionesPedidos(api: ClienteApi) {
    return {
        // ---- Eventos (lo que leen Pedidos, Informes y Taquilla) ----
        // ---- Pedidos (10.6) ----
        /** listTicketOrders (`entradas.pedidos.ver`): con «lo suyo», solo los del código del RRPP. */
        listTicketOrders: (businessId: string, filtros: FiltrosPedidos = {}) => api.pedir<ListaPedidos>('GET', r(businessId, `/ticket-orders${consulta({ ...filtros })}`)),
        /** getTicketOrder con su `ETag` (para corregir un nombre y cancelar). */
        getTicketOrder: (businessId: string, orderId: string): Promise<ConVersion<Pedido>> => api.pedirConVersion<Pedido>('GET', r(businessId, `/ticket-orders/${id(orderId)}`)),
        /** updateTicketHolder (`entradas.pedidos.gestionar`, `If-Match`): el pase pasa a una versión nueva con el mismo QR (G508). */
        updateTicketHolder: (businessId: string, orderId: string, ticketId: string, nombre: string, version: string, clave = claveIdempotencia()) =>
            api.pedir<Entrada>('PATCH', r(businessId, `/ticket-orders/${id(orderId)}/tickets/${id(ticketId)}`), { cuerpo: { holder_name: nombre }, siCoincide: version, idempotencia: clave }),
        /** cancelTicketOrder (`entradas.pedidos.reembolsar`, `If-Match`): `full`, `without_booking_fee` o `none_external`. */
        cancelTicketOrder: (businessId: string, orderId: string, datos: CancelarPedido, version: string, clave = claveIdempotencia()) =>
            api.pedir<Pedido>('POST', r(businessId, `/ticket-orders/${id(orderId)}/cancellation`), { cuerpo: datos, siCoincide: version, idempotencia: clave }),
        /** retryTicketOrderRefund (`entradas.pedidos.reembolsar`): el mismo importe otra vez. */
        retryTicketOrderRefund: (businessId: string, orderId: string, clave = claveIdempotencia()) =>
            api.pedir<Pedido>('POST', r(businessId, `/ticket-orders/${id(orderId)}/refund-retries`), { idempotencia: clave }),
        // ---- Taquilla (10.7) ----
        /** getBoxOfficeStatus (`entradas.taquilla.vender`): tipos con color (G786), plazas, cupo y la parte de la tablet. */
        getBoxOfficeStatus: (businessId: string, eventId: string) => api.pedir<EstadoTaquilla>('GET', r(businessId, `/events/${id(eventId)}/box-office`)),
        /** quoteTicketOrder: el importe de lo elegido lo calcula la API, no el panel. */
        quoteTicketOrder: (businessId: string, eventId: string, lineas: LineaPedida[], clave = claveIdempotencia()) =>
            api.pedir<Presupuesto>('POST', r(businessId, '/ticket-order-quotes'), { cuerpo: { event_id: eventId, lines: lineas }, idempotencia: clave }),
        /** createBoxOfficeSale (`entradas.taquilla.vender`): `cash` o `card_terminal`; solo se anota la forma de pago (G5). */
        createBoxOfficeSale: (businessId: string, eventId: string, venta: VentaTaquilla, clave = claveIdempotencia()) =>
            api.pedir<PedidoCreado>('POST', r(businessId, `/events/${id(eventId)}/box-office-sales`), { cuerpo: venta, idempotencia: clave }),

        // ---- Informes (10.8) ----
        /** getEventSalesSummary (`entradas.informes.ver`): aforo, ventas por tipo y canal, ingresos sin gastos de gestión. */
        getEventSalesSummary: (businessId: string, eventId: string) => api.pedir<ResumenVentas>('GET', r(businessId, `/events/${id(eventId)}/sales-summary`)),
    };
}

export type OperacionesPedidos = ReturnType<typeof operacionesPedidos>;
